import React, { useEffect, useState } from 'react';
import '../styles/components/qr-scanner.css';

interface UploadBarcodeViewProps {
  onCancel: () => void;
}

/* ── Barcode SVG paths (same as QRBarcodeScanner) ── */
const barcodePaths = [
  "M34.2773 0H37.8974V36.2938H34.2773V0Z",
  "M53.8457 0H57.4657V36.2938H53.8457V0Z",
  "M60.2811 4.98246e-05H63.9311V36.2938H60.2811V4.98246e-05Z",
  "M9.62493 4.98246e-05H13.1871V36.249H9.62493V4.98246e-05Z",
  "M18.5332 0.000976562H26.1474V36.2945L18.5332 36.2492V0.000976562Z",
  "M43.3867 0H50.9724V36.2938H43.3867V0Z",
  "M29.8828 0H31.6349V36.2938H29.8828V0Z",
  "M0 0H7.69958V36.2489H0V0Z",
];

/* ── QR icon SVG paths (same as QRBarcodeScanner) ── */
const qrIconPaths = [
  "M3.125 3.125H9.375V0H0V9.375H3.125V3.125Z",
  "M30.625 0V3.125H36.875V9.375H40V0H30.625Z",
  "M3.125 30.625H0V40H9.375V36.875H3.125V30.625Z",
  "M36.875 36.875H30.625V40H40V30.625H36.875V36.875Z",
  "M5.9375 18.4375H18.4375V5.9375H5.9375V18.4375ZM9.0625 9.0625H15.3125V15.3125H9.0625V9.0625Z",
  "M34.0625 5.9375H21.5625V18.4375H34.0625V5.9375ZM30.9375 15.3125H24.6875V9.0625H30.9375V15.3125Z",
  "M5.9375 34.0625H18.4375V21.5625H5.9375V34.0625ZM9.0625 24.6875H15.3125V30.9375H9.0625V24.6875Z",
  "M24.6875 24.6875V21.5625H21.5625V27.8125H24.6875V30.9375H27.8125V24.6875H24.6875Z",
  "M27.8125 30.9375H34.0625V34.0625H27.8125V30.9375Z",
  "M30.9375 27.8125H34.0625V21.5625H27.8125V24.6875H30.9375V27.8125Z",
  "M21.5625 30.9375H24.6875V34.0625H21.5625V30.9375Z",
];

/* ── Corner bracket styles ── */
const cornerStyles: React.CSSProperties[] = [
  { top: 0, left: 0, borderTop: '3px solid #217C70', borderLeft: '3px solid #217C70', borderRadius: '6px 0 0 0' },
  { top: 0, right: 0, borderTop: '3px solid #217C70', borderRight: '3px solid #217C70', borderRadius: '0 6px 0 0' },
  { bottom: 0, left: 0, borderBottom: '3px solid #217C70', borderLeft: '3px solid #217C70', borderRadius: '0 0 0 6px' },
  { bottom: 0, right: 0, borderBottom: '3px solid #217C70', borderRight: '3px solid #217C70', borderRadius: '0 0 6px 0' },
];

const UploadBarcodeView: React.FC<UploadBarcodeViewProps> = ({ onCancel }) => {
  const [progress, setProgress] = useState(0);

  /* ── Scan line animation (JS-driven, 60fps) ── */
  const [scanY, setScanY] = useState(20);
  useEffect(() => {
    let y = 20, dir = 1;
    const id = setInterval(() => {
      y += dir * 0.8;
      if (y >= 78) { y = 78; dir = -1; }
      if (y <= 20) { y = 20; dir = 1; }
      setScanY(y);
    }, 16);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 300);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-container">
        {/* Header */}
        <div className="qr-scanner-header">
          <button onClick={onCancel} className="qr-scanner-back-btn">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <h3 className="qr-scanner-header-title">Add New Device</h3>
        </div>

        {/* Body */}
        <div className="qr-scanner-body">
          {/* QR icon */}
          <div className="qr-scanner-icon-wrap">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              {qrIconPaths.map((d, i) => (
                <path key={i} d={d} fill="#487A9F" />
              ))}
            </svg>
          </div>

          <h2 className="qr-scanner-title">Scanning QR Code</h2>
          <p className="qr-scanner-subtitle">
            Upload/Scan the QR code from the back of the device
          </p>

          {/* Scanner box — dark area with barcode + scan line */}
          <div className="qr-scanner-dark-box">
            {/* Noise texture */}
            <div className="qr-scanner-noise" />
            {/* Vignette */}
            <div className="qr-scanner-vignette" />

            {/* Scan window frame */}
            <div className="qr-scanner-frame">
              {/* Barcode placeholder */}
              <div className="qr-scanner-barcode">
                <svg width="96" height="56" viewBox="0 0 63.9312 36.2945" fill="none">
                  {barcodePaths.map((d, i) => (
                    <path key={i} d={d} fill="white" />
                  ))}
                </svg>
              </div>

              {/* Illuminated strip */}
              <div
                className="qr-scanner-illumination"
                style={{ top: `calc(${scanY}% - 14px)` }}
              />

              {/* Corner brackets */}
              {cornerStyles.map((s, i) => (
                <div key={i} className="qr-scanner-corner" style={s} />
              ))}

              {/* Scan line */}
              <div className="qr-scanner-line" style={{ top: `${scanY}%` }} />

              {/* Glow above scan line */}
              <div
                className="qr-scanner-line-glow"
                style={{ top: `${scanY}%` }}
              />
            </div>

            {/* Pulsing ring */}
            <div className="qr-scanner-pulse-ring" />
          </div>

          {/* Progress section */}
          <div className="qr-scanner-progress">
            <span className="qr-scanner-progress-label">Fetching Device Info</span>
            <div className="qr-scanner-progress-track">
              <div
                className="qr-scanner-progress-fill"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="qr-scanner-bottom-bar">
          <button onClick={onCancel} className="qr-scanner-cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadBarcodeView;
